import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";
import { DEFAULT_MOVEMENTS_BY_SECTION } from "./config";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { useTranslation } from "react-i18next";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";

const filterN1TransactionsSchema = z.object({
  n1_portfolio_client_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  n1_portfolio_vehicle_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  n1_portfolio_account_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  n1_portfolio_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  n1_portfolio_bank_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  actif_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
  category_contains: z.string().optional(),
  transfer_contains: z.string().optional(),
  fee_type_contains: z.string().optional(),
  description_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  n1_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
});
const filterN1TransactionsFields = filterN1TransactionsSchema.keyof().enum;

export type FilterN1TransactionsSchema = z.infer<
  typeof filterN1TransactionsSchema
>;

const portfolioDepFields: readonly DepField<FilterN1TransactionsSchema>[] = [
  {
    name: filterN1TransactionsFields.n1_portfolio_bank_name_contains,
    key: "bank_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterN1TransactionsFields.n1_portfolio_client_contains,
    key: "client",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterN1TransactionsFields.n1_portfolio_vehicle_contains,
    key: "vehicle",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterN1TransactionsFields.n1_portfolio_name_contains,
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterN1TransactionsFields.n1_portfolio_account_name_contains,
    key: "account_name",
    extract: "text",
    bucket: "equals",
  },
];

export function FilterN1Transactions() {
  const { t, i18n } = useTranslation("tables");
  const tableName = `${Tables.N1_TRANSACTIONS}`;

  const fields: Field[] = [
    {
      name: filterN1TransactionsFields.n1_portfolio_client_contains,
      label: t(`${tableName}.searchbox.n1_portfolio_client_contains`, { defaultValue: "Client" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.N1}/${Tables.N1_PORTFOLIOS}`,
      column_name: "client",
      filters: makeFiltersBuilder<FilterN1TransactionsSchema>(
        portfolioDepFields,
        filterN1TransactionsFields.n1_portfolio_client_contains,
      ),
    },
    {
      name: filterN1TransactionsFields.n1_portfolio_vehicle_contains,
      label: t(`${tableName}.searchbox.n1_portfolio_vehicle_contains`, { defaultValue: "Vehicle / Beneficiary" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.N1}/${Tables.N1_PORTFOLIOS}`,
      column_name: "vehicle",
      filters: makeFiltersBuilder<FilterN1TransactionsSchema>(
        portfolioDepFields,
        filterN1TransactionsFields.n1_portfolio_vehicle_contains,
      ),
    },
    {
      name: filterN1TransactionsFields.n1_portfolio_account_name_contains,
      label: t(`${tableName}.searchbox.n1_portfolio_account_name_contains`, { defaultValue: "Account group" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.N1}/${Tables.N1_PORTFOLIOS}`,
      column_name: "account_name",
      filters: makeFiltersBuilder<FilterN1TransactionsSchema>(
        portfolioDepFields,
        filterN1TransactionsFields.n1_portfolio_account_name_contains,
      ),
    },
    {
      name: filterN1TransactionsFields.n1_portfolio_name_contains,
      label: t(`${tableName}.searchbox.n1_portfolio_name_contains`, { defaultValue: "Account" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.N1}/${Tables.N1_PORTFOLIOS}`,
      column_name: "name",
      filters: makeFiltersBuilder<FilterN1TransactionsSchema>(
        portfolioDepFields,
        filterN1TransactionsFields.n1_portfolio_name_contains,
      ),
    },
    {
      name: filterN1TransactionsFields.n1_portfolio_bank_name_contains,
      label: t(`${tableName}.searchbox.n1_portfolio_bank_name_contains`, { defaultValue: "Bank" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.N1}/${Tables.N1_PORTFOLIOS}`,
      column_name: "bank_name",
      filters: makeFiltersBuilder<FilterN1TransactionsSchema>(
        portfolioDepFields,
        filterN1TransactionsFields.n1_portfolio_bank_name_contains,
      ),
    },
    {
      name: filterN1TransactionsFields.actif_contains,
      label: t(`${tableName}.searchbox.actif_contains`, { defaultValue: "Asset" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.N1}/${Tables.N1_STATIC_DATA}`,
      column_name: "name",
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: filterN1TransactionsFields.asset_class_1_equals,
      assetSubclassFieldName: filterN1TransactionsFields.asset_class_2_equals,
      assetClassLabel: t(`${tableName}.searchbox.asset_class_1_equals`),
      assetSubclassLabel: t(`${tableName}.searchbox.asset_class_2_equals`),
      client: Clients.N1,
      staticDataTableName: `${Tables.N1_STATIC_DATA}`,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      assetSubclassFieldType: "select",
    }),
    {
      name: filterN1TransactionsFields.category_contains,
      label: t(`${tableName}.searchbox.category_contains`, { defaultValue: "Transaction type" }),
      type: "sectionSelect",
      useTranslation: true,
      section_name_translation_prefix: "transactions.category_groups",
      translate_key: "category",
      client: Clients.N1,
      table_name: `${Tables.N1_TRANSACTIONS}`,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      options: DEFAULT_MOVEMENTS_BY_SECTION,
    },
    {
      name: filterN1TransactionsFields.transfer_contains,
      label: t(`${tableName}.searchbox.transfer_contains`, { defaultValue: "Transfer type" }),
      type: "select",
      useTranslation: true,
      client: Clients.N1,
      table_name: `${Tables.N1_TRANSACTIONS}`,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "transfer",
    },
    {
      name: filterN1TransactionsFields.fee_type_contains,
      label: t(`${tableName}.searchbox.fee_type_contains`, { defaultValue: "Fee type" }),
      type: "select",
      useTranslation: true,
      client: Clients.N1,
      table_name: `${Tables.N1_TRANSACTIONS}`,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "fee_type",
    },
    {
      name: filterN1TransactionsFields.description_contains,
      label: t(`${tableName}.searchbox.description_contains`, { defaultValue: "Description" }),
      type: "input",
    },
    {
      name: "date",
      type: "dateRange",
      label: t(`${tableName}.searchbox.date`, { defaultValue: "Value date" }),
      from: { name: filterN1TransactionsFields.date_from },
      to: { name: filterN1TransactionsFields.date_to },
      col_span: 2,
    },
    {
      name: "create",
      type: "dateRange",
      label: t(`${tableName}.searchbox.create`, { defaultValue: "Creation date" }),
      from: { name: filterN1TransactionsFields.create_from },
      to: { name: filterN1TransactionsFields.create_to },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      columns={2}
      client={Clients.N1}
      schema={filterN1TransactionsSchema}
      tableName={`${Tables.N1_TRANSACTIONS}`}
      fields={fields}
    />
  );
}
